/**
 * CronExpressionInput Component
 *
 * Input component for cron expressions with preset options and timezone selection.
 */

import React from 'react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import { FormField } from '../../../components/forms/FormField';

interface CronExpressionInputProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  timezone?: string;
  onTimezoneChange?: (timezone: string) => void;
  error?: string;
}

interface CronPreset {
  label: string;
  value: string;
  description: string;
}

const CRON_PRESETS: CronPreset[] = [
  { label: 'Hourly', value: '0 * * * *', description: 'Every hour at minute 0' },
  { label: 'Daily', value: '0 0 * * *', description: 'Every day at midnight' },
  { label: 'Weekly', value: '0 0 * * 0', description: 'Every Sunday at midnight' },
  { label: 'Monthly', value: '0 0 1 * *', description: 'First day of month at midnight' },
];

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York (Eastern Time)' },
  { value: 'America/Chicago', label: 'America/Chicago (Central Time)' },
  { value: 'America/Denver', label: 'America/Denver (Mountain Time)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific Time)' },
  { value: 'America/Santiago', label: 'America/Santiago (Chile)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (Brazil)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
];

const parseCronExpression = (cron: string): string => {
  // Simple parser for common patterns
  const parts = cron.split(' ');
  if (parts.length !== 5) return 'Invalid cron expression';

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Check for presets
  if (cron === '0 * * * *') return 'Every hour at minute 0';
  if (cron === '0 0 * * *') return 'Every day at midnight';
  if (cron === '0 0 * * 0') return 'Every Sunday at midnight';
  if (cron === '0 0 1 * *') return 'First day of each month at midnight';

  // Build description
  const descriptions: string[] = [];

  // Minutes
  if (minute === '*') {
    descriptions.push('every minute');
  } else if (minute.includes('/')) {
    const interval = minute.split('/')[1];
    descriptions.push(`every ${interval} minutes`);
  } else {
    descriptions.push(`at minute ${minute}`);
  }

  // Hours
  if (hour !== '*') {
    if (hour.includes('/')) {
      const interval = hour.split('/')[1];
      descriptions.push(`every ${interval} hours`);
    } else if (hour.includes('-')) {
      descriptions.push(`between hours ${hour}`);
    } else {
      descriptions.push(`at ${hour}:00`);
    }
  }

  // Day of month
  if (dayOfMonth !== '*') {
    descriptions.push(`on day ${dayOfMonth}`);
  }

  // Month
  if (month !== '*') {
    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNum = parseInt(month);
    if (monthNum >= 1 && monthNum <= 12) {
      descriptions.push(`in ${monthNames[monthNum]}`);
    }
  }

  // Day of week
  if (dayOfWeek !== '*') {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayNum = parseInt(dayOfWeek);
    if (dayNum >= 0 && dayNum <= 6) {
      descriptions.push(`on ${dayNames[dayNum]}`);
    }
  }

  return descriptions.join(', ') || 'Custom schedule';
};

export const CronExpressionInput: React.FC<CronExpressionInputProps> = ({
  value,
  onChange,
  timezone = 'UTC',
  onTimezoneChange,
  error,
}) => {
  const isManualOnly = !value;

  const handleManualOnlyChange = (checked: boolean) => {
    if (checked) {
      // Use empty string instead of undefined so react-hook-form
      // properly detects the value change and triggers a re-render.
      // Empty string is falsy, so isManualOnly = !value still works.
      onChange('');
    } else {
      onChange('0 0 * * *'); // Default to daily
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Checkbox
          checked={isManualOnly}
          onChange={(e) => handleManualOnlyChange(e.target.checked)}
        />
        <span className="text-sm text-gray-700">Manual trigger only (no schedule)</span>
      </div>

      {!isManualOnly && (
        <>
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            {CRON_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                type="button"
                variant={value === preset.value ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => onChange(preset.value)}
                title={preset.description}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Custom Input */}
          <FormField
            label="Cron Expression"
            hint="Format: minute hour day-of-month month day-of-week"
            error={error}
          >
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value || undefined)}
              placeholder="0 0 * * *"
              className="font-mono"
              error={!!error}
            />
          </FormField>

          {/* Timezone Selector */}
          {onTimezoneChange && (
            <FormField
              label="Timezone"
              hint="Schedule will be interpreted in this timezone"
            >
              <Select
                value={timezone}
                onChange={(e) => onTimezoneChange(e.target.value)}
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </Select>
            </FormField>
          )}

          {/* Human-readable Preview */}
          {value && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Schedule: </span>
                {parseCronExpression(value)}
                {timezone && timezone !== 'UTC' && (
                  <span className="text-blue-600"> ({timezone})</span>
                )}
                {(!timezone || timezone === 'UTC') && (
                  <span className="text-blue-600"> (UTC)</span>
                )}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
